// ─────────────────────────────────────────────────────────────────────────────
// OUTFORM — CONCEPT 4: COMMAND PANEL
//
// Atlas draft, 25 Aug 2026. Sibling of concept3 (Review Queue) and concept5
// (Activity Feed) — all three exploring different docked-panel interaction
// models for Outform's estimating sheet, none of them a chat thread.
//
// The bet this one makes: no conversation at all, not even a narrow one. The
// side panel is a FIXED, SMALL set of labeled action buttons — a toolbar, not
// a composer. Each button is a single AI-powered operation that runs against
// the whole open document. There is no growing message history to scroll —
// pressing a button replaces the one result line with a new one, and a small
// number of transient actions can ride along with THAT specific result (an
// "apply this fix" or an "accept / hold" pair), but they are attached to the
// result that produced them, not entries in a thread. This is the opposite
// bet from concept3's cell-level Cmd-K popover: nothing here is scoped to a
// selection by typing a question — selection only chooses WHAT a fixed button
// acts on, never WHAT to ask.
//
// ── WHAT THIS IS, PLAINLY ────────────────────────────────────────────────
// A click-through visual demo, not a narrated video — same approach as
// concept3: Play advances on a fixed timer, Prev/Next/dots are the scrub
// controls, the caption under the controls is plain text. No narration audio.
//
// ── BOUNDARIES ───────────────────────────────────────────────────────────
// This file imports NOTHING from board/outform/ (that tree is Quinn's, for
// Outform's real board) and nothing outside this file imports FROM here. The
// mock sheet below is a styled grid, not the real Univer library — this is a
// design comparison, not a build. Own CSS, own prefix (`.oc4-`), so nothing
// can collide with concept3's `.oc3-*` or Quinn's `.of-*` classes even though
// the visual language (clean grid, docked side panel) is deliberately the
// same family.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import "./outform-concept4.css";

const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

// ── the job — same illustrative Nordstrom endcap job as concept3, so the
// numbers a viewer sees across concepts don't contradict each other ─────────
const UNITS = 340;
const ASSEMBLY_RATE_ORIG = 46.5;
const PRICE_ORIGINAL = 158900;
const PRICE_PUSHBACK = 145000;
const MARGIN_FLOOR = 0.55;

type ItemRow = { kind: "item"; id: string; label: string; qty: number; unit: string; rate: number };
type RowDef = { kind: "colhead" } | { kind: "section"; label: string } | ItemRow;

const LABOR_IDS = ["assembly", "fabrication", "finishing", "qc", "install"];

const SHEET_ROWS: RowDef[] = [
  { kind: "colhead" },
  { kind: "section", label: "Labor" },
  { kind: "item", id: "assembly", label: "Assembly labor", qty: UNITS, unit: "hrs", rate: ASSEMBLY_RATE_ORIG },
  { kind: "item", id: "fabrication", label: "Fabrication labor", qty: UNITS, unit: "hrs", rate: 34.0 },
  { kind: "item", id: "finishing", label: "Finishing labor", qty: UNITS, unit: "hrs", rate: 19.25 },
  { kind: "item", id: "qc", label: "QC & inspection", qty: UNITS, unit: "hrs", rate: 8.4 },
  { kind: "item", id: "install", label: "Install labor", qty: UNITS, unit: "hrs", rate: 6.1 },
  { kind: "section", label: "Materials" },
  { kind: "item", id: "base", label: "ABS moulded base", qty: UNITS, unit: "ea", rate: 28.4 },
  { kind: "item", id: "acrylic", label: "Acrylic panel, edge-lit", qty: UNITS, unit: "ea", rate: 41.9 },
  { kind: "item", id: "led", label: "LED module + driver", qty: UNITS, unit: "ea", rate: 22.15 },
  { kind: "item", id: "hardware", label: "Hardware & fasteners", qty: UNITS, unit: "ea", rate: 3.6 },
];

// the 3 comparable jobs "Compare to Past Jobs" cites
const COMPS = [
  { job: "Sephora · Rouge endcap", rate: 37.8 },
  { job: "Ulta · Fragrance bay", rate: 38.1 },
  { job: "Nike · Kids footwear wall", rate: 37.4 },
];
const COMP_AVG = COMPS.reduce((s, c) => s + c.rate, 0) / COMPS.length;
const COMP_PCT_OVER = Math.round((ASSEMBLY_RATE_ORIG / COMP_AVG - 1) * 100);

function rateFor(row: ItemRow, assemblyFixed: boolean): number {
  return row.id === "assembly" && assemblyFixed ? COMP_AVG : row.rate;
}
function totalCost(assemblyFixed: boolean): number {
  return SHEET_ROWS.reduce((sum, r) => (r.kind === "item" ? sum + r.qty * rateFor(r, assemblyFixed) : sum), 0);
}

type OpKey = "build" | "compare" | "recalc" | "flag";
const BUTTONS: { key: OpKey; label: string; hint: string }[] = [
  { key: "build", label: "Build Ballpark", hint: "Price the whole job from the brief" },
  { key: "compare", label: "Compare to Past Jobs", hint: "Check the selection against comps" },
  { key: "recalc", label: "Recalculate at New Price", hint: "Re-run margin at a target price" },
  { key: "flag", label: "Flag Underpriced Lines", hint: "Scan every row against the margin floor" },
];

type Step = { n: number; title: string; note: string };
const STEPS: Step[] = [
  { n: 1, title: "An empty job", note: "Plain spreadsheet on the left, four fixed moves on the right — nothing pressed yet." },
  { n: 2, title: "A brief lands", note: "An RFQ is dropped on the sheet. It's context now, not used yet." },
  { n: 3, title: "Build Ballpark, pressed", note: "One button spins. Nothing else in the panel moves." },
  { n: 4, title: "The sheet does the work", note: "Labor rows land, then materials — the motion is on the sheet, not the panel." },
  { n: 5, title: "One result line", note: "Ballpark built. Reasoning is a toggle away, not a transcript." },
  { n: 6, title: "A rate looks high", note: "Select the row, reach for Compare to Past Jobs." },
  { n: 7, title: "The comparison runs", note: "One rate is the outlier — a fix rides along with the answer." },
  { n: 8, title: "Applied, then it's gone", note: "The cell updates, the result confirms it, then settles back to the ballpark." },
  { n: 9, title: "A price pushback", note: "Recalculate at New Price opens one field, right here in the panel." },
  { n: 10, title: "Confirmed — recalculating", note: "$145,000 — margin is moving across every line." },
  { n: 11, title: "Under the floor", note: "Margin misses the 55% rule. The result line carries a warning." },
  { n: 12, title: "A decision, attached", note: "Accept anyway or hold for review — tied to this result, not a thread." },
  { n: 13, title: "Held for review", note: "Confirmed on the result line. A small badge lands on the sheet." },
  { n: 14, title: "No thread to scroll", note: "Four fixed moves, not a conversation to have." },
];

export function OutformConceptFour({ width = 1710 }: { width?: number }) {
  const APP_W = 1680;
  const APP_H = 1000;
  const scale = width / APP_W;

  const [step, setStep] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  const s = STEPS[step - 1];

  const advance = useCallback(() => {
    setStep((cur) => {
      if (cur >= STEPS.length) {
        setPlaying(false);
        return cur;
      }
      return cur + 1;
    });
  }, []);

  const go = useCallback((n: number) => setStep(Math.max(1, Math.min(STEPS.length, n))), []);

  // ── PLAY — a fixed timer, not audio. Same rationale as concept3: this pass
  // is honest about being a click-through and advances on a clock.
  const STEP_MS = 3400;
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => advance(), STEP_MS);
    return () => window.clearInterval(t);
  }, [playing, advance]);

  // the reasoning toggle is per-result, not global — collapse it whenever the
  // step (and so the result on screen) changes.
  useEffect(() => setReasoningOpen(false), [step]);

  // reveal-in animation, same technique as concept3: any element tagged
  // `.oc4-at-N` fades/slides in when the deck reaches step N.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const q = (sel: string) => Array.from(el.querySelectorAll(sel)) as HTMLElement[];
    const m = q(`.oc4-at-${step}`);
    if (m.length) animate(m, { opacity: [0, 1], y: [10, 0], duration: 400, delay: stagger(90) });
    if (step === 4) {
      const rows = q(".oc4-row.is-labor");
      if (rows.length) animate(rows, { opacity: [0, 1], x: [-10, 0], duration: 300, delay: stagger(70) });
    }
    if (step === 8) {
      const toast = q(".oc4-toast");
      if (toast.length) animate(toast, { opacity: [0, 1], y: [-6, 0], duration: 260 });
    }
  }, [step]);

  // ── derived state, one flag per beat of the arc ─────────────────────────
  const fileDropped = step >= 2;
  const phase: "empty" | "partial" | "full" = step <= 3 ? "empty" : step === 4 ? "partial" : "full";
  const workingOp: OpKey | null = step === 3 || step === 4 ? "build" : step === 6 ? "compare" : step === 10 ? "recalc" : null;

  const assemblyState: "" | "flag" | "outlier" | "fixed" = step === 6 ? "flag" : step === 7 ? "outlier" : step >= 8 ? "fixed" : "";
  const assemblySelected = step >= 6 && step <= 8;
  const assemblyFixed = step >= 8;

  const cost = totalCost(assemblyFixed);
  const price = step >= 10 ? PRICE_PUSHBACK : PRICE_ORIGINAL;
  const margin = (price - cost) / price;
  const marginBeforeRecalc = (PRICE_ORIGINAL - totalCost(true)) / PRICE_ORIGINAL;
  const held = step >= 13;
  const closing = step === 14;

  const resultKind: "none" | "ballpark" | "compare" | "applied" | "recalc-prompt" | "recalc-running" | "recalc-warn" | "held" =
    step <= 4
      ? "none"
      : step <= 6
        ? "ballpark"
        : step === 7
          ? "compare"
          : step === 8
            ? "applied"
            : step === 9
              ? "recalc-prompt"
              : step === 10
                ? "recalc-running"
                : step <= 12
                  ? "recalc-warn"
                  : "held";

  return (
    <div style={{ width }}>
      <div className="nowheel nodrag nopan" style={{ width, height: APP_H * scale, overflow: "hidden", borderRadius: 14 }}>
        <div
          ref={root}
          className="oc4-root"
          style={{ width: APP_W, height: APP_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          <div className="oc4-flag">
            Design prototype · illustrative numbers · draft concept for comparison, not the shipped UI · the
            spreadsheet below is a mock, not the real Univer sheet
          </div>

          {closing ? (
            <Closing />
          ) : (
            <div className="oc4-app" dir="ltr">
              <section className="oc4-sheetpane">
                <SheetHead phase={phase} held={held} fileDropped={fileDropped} />
                <div className="oc4-sheetbody">
                  {phase === "empty" ? (
                    <Empty step={step} fileDropped={fileDropped} />
                  ) : (
                    <>
                      <div className="oc4-grid">
                        {SHEET_ROWS.map((def, i) => (
                          <Row
                            key={i}
                            def={def}
                            assemblyFixed={assemblyFixed}
                            assemblyState={assemblyState}
                            assemblySelected={assemblySelected}
                            partial={phase === "partial"}
                            onSelect={() => {
                              if (def.kind === "item" && def.id === "assembly" && step === 5) go(6);
                            }}
                          />
                        ))}
                        {phase === "partial" ? (
                          <div className="oc4-skel">
                            <span className="oc4-skel-pulse" /> Materials, margin and total — still building…
                          </div>
                        ) : (
                          <div className="oc4-summary">
                            <div className={`oc4-summary-row ${margin < MARGIN_FLOOR && step >= 10 ? "is-warn" : ""}`}>
                              <span>Margin</span>
                              <b>{(margin * 100).toFixed(1)}%</b>
                              <em>{margin < MARGIN_FLOOR && step >= 10 ? `under the ${Math.round(MARGIN_FLOOR * 100)}% floor` : "on target"}</em>
                            </div>
                            <div className="oc4-summary-row">
                              <span>Total price</span>
                              <b>{money(price)}</b>
                              <em>cost {money(cost)}</em>
                            </div>
                            {held ? (
                              <div className="oc4-held-badge oc4-at-13">
                                <span />
                                Held for review
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </section>

              <section className="oc4-panel">
                <div className="oc4-panel-head">
                  <b>Command panel</b>
                  <span>Fixed moves · not a conversation</span>
                </div>

                <div className="oc4-buttons">
                  {BUTTONS.map((b) => (
                    <ActionButton
                      key={b.key}
                      btn={b}
                      working={workingOp === b.key}
                      disabled={workingOp !== null && workingOp !== b.key}
                      onClick={() => {
                        if (b.key === "build" && step < 3) go(3);
                        if (b.key === "compare" && step === 6) go(7);
                        if (b.key === "recalc" && step < 9) go(9);
                      }}
                    />
                  ))}
                </div>

                <div className="oc4-result-wrap">
                  <ResultPanel
                    kind={resultKind}
                    reasoningOpen={reasoningOpen}
                    onToggleReasoning={() => setReasoningOpen((r) => !r)}
                    price={price}
                    margin={margin}
                    marginBeforeRecalc={marginBeforeRecalc}
                    onApplyAverage={() => go(8)}
                    onConfirmRecalc={() => go(10)}
                    onAcceptAnyway={() => {}}
                    onHoldForReview={() => go(13)}
                    showActions={step >= 12}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      <div className="oc4-ctl nowheel nodrag nopan">
        <button onClick={() => go(step - 1)} disabled={step === 1}>
          ‹
        </button>
        <div className="oc4-ctl-dots">
          {STEPS.map((x) => (
            <button key={x.n} className={x.n === step ? "on" : ""} onClick={() => go(x.n)} title={x.title} />
          ))}
        </div>
        <button onClick={() => go(step + 1)} disabled={step === STEPS.length}>
          ›
        </button>
        <button className="oc4-play" onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pause" : "Play"}
        </button>
        <div className="oc4-ctl-text">
          <b>
            {step} / {STEPS.length} — {s.title}
          </b>
          <span>{s.note}</span>
        </div>
      </div>
    </div>
  );
}

// ── the empty state ──────────────────────────────────────────────────────
function Empty({ step, fileDropped }: { step: number; fileDropped: boolean }) {
  return (
    <div className="oc4-empty">
      <div className="oc4-empty-icon">+</div>
      <h3>No job open yet</h3>
      <p>
        {step === 1
          ? "Drop a brief onto the sheet to get started."
          : step === 2
            ? "Brief attached — press Build Ballpark to price it."
            : "Reading the brief…"}
      </p>
      {fileDropped ? (
        <div className="oc4-file oc4-at-2">
          <span className="oc4-file-ic">PDF</span>
          <div>
            <b>Nordstrom_Denim_Endcap_RFQ_v2.pdf</b>
            <span>Attached — not used yet</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SheetHead({
  phase,
  held,
  fileDropped,
}: {
  phase: "empty" | "partial" | "full";
  held: boolean;
  fileDropped: boolean;
}) {
  return (
    <div className="oc4-sheethead">
      <div className="oc4-sheethead-title">
        <b>{phase === "empty" ? "New estimate" : "Nordstrom · Denim Bay End-cap"}</b>
        <em>{phase === "empty" ? (fileDropped ? "Nordstrom_Denim_Endcap_RFQ_v2.pdf" : "—") : "NB-2201_Estimate · 340 units"}</em>
      </div>
      {phase === "full" ? (
        held ? (
          <span className="oc4-ready is-held">On hold</span>
        ) : (
          <span className="oc4-ready">Ballpark ready</span>
        )
      ) : null}
    </div>
  );
}

function Row({
  def,
  assemblyFixed,
  assemblyState,
  assemblySelected,
  partial,
  onSelect,
}: {
  def: RowDef;
  assemblyFixed: boolean;
  assemblyState: "" | "flag" | "outlier" | "fixed";
  assemblySelected: boolean;
  partial: boolean;
  onSelect: () => void;
}) {
  if (def.kind === "colhead") {
    return (
      <div className="oc4-row oc4-row-head">
        <span className="oc4-c oc4-c-label">Item</span>
        <span className="oc4-c oc4-c-qty">Qty</span>
        <span className="oc4-c oc4-c-unit">Unit</span>
        <span className="oc4-c oc4-c-rate">Rate</span>
        <span className="oc4-c oc4-c-ext">Ext. cost</span>
      </div>
    );
  }
  if (def.kind === "section") {
    if (partial && def.label === "Materials") {
      return null; // materials section header stays hidden while still "building"
    }
    return (
      <div className="oc4-row oc4-row-section">
        <span className="oc4-c oc4-c-label">{def.label}</span>
      </div>
    );
  }

  const isLabor = LABOR_IDS.includes(def.id);
  if (partial && !isLabor) return null; // materials rows hidden during the partial build

  const isAssembly = def.id === "assembly";
  const rate = rateFor(def, assemblyFixed);
  const ext = def.qty * rate;
  const state = isAssembly ? assemblyState : "";
  const selected = isAssembly && assemblySelected;

  const cls = [
    "oc4-row",
    isLabor && "is-labor",
    selected && "is-selected",
    state === "flag" && "is-flag",
    state === "outlier" && "is-outlier",
    state === "fixed" && "is-fixed",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} onClick={isAssembly ? onSelect : undefined} role={isAssembly ? "button" : undefined}>
      <span className="oc4-c oc4-c-label">
        {def.label}
        {state === "fixed" ? (
          <span
            className="oc4-comment-badge"
            title={`Rate corrected from $${ASSEMBLY_RATE_ORIG.toFixed(2)}/hr to $${COMP_AVG.toFixed(
              2,
            )}/hr — comp average across 3 similar jobs.`}
          />
        ) : null}
      </span>
      <span className="oc4-c oc4-c-qty">{def.qty}</span>
      <span className="oc4-c oc4-c-unit">{def.unit}</span>
      <span className="oc4-c oc4-c-rate">${rate.toFixed(2)}</span>
      <span className="oc4-c oc4-c-ext">{money(ext)}</span>
    </div>
  );
}

// ── the action buttons — the whole point of this concept ────────────────
function ActionButton({
  btn,
  working,
  disabled,
  onClick,
}: {
  btn: { key: OpKey; label: string; hint: string };
  working: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`oc4-btn ${working ? "is-working" : ""} ${disabled ? "is-disabled" : ""}`} onClick={onClick} disabled={disabled}>
      <span className="oc4-btn-top">
        <b>{btn.label}</b>
        {working ? <span className="oc4-spinner" /> : null}
      </span>
      <span className="oc4-btn-hint">{btn.hint}</span>
    </button>
  );
}

// ── the result area — one line, replaced, never accumulated ─────────────
function ResultPanel({
  kind,
  reasoningOpen,
  onToggleReasoning,
  price,
  margin,
  marginBeforeRecalc,
  onApplyAverage,
  onConfirmRecalc,
  onAcceptAnyway,
  onHoldForReview,
  showActions,
}: {
  kind: "none" | "ballpark" | "compare" | "applied" | "recalc-prompt" | "recalc-running" | "recalc-warn" | "held";
  reasoningOpen: boolean;
  onToggleReasoning: () => void;
  price: number;
  margin: number;
  marginBeforeRecalc: number;
  onApplyAverage: () => void;
  onConfirmRecalc: () => void;
  onAcceptAnyway: () => void;
  onHoldForReview: () => void;
  showActions: boolean;
}) {
  if (kind === "none") {
    return (
      <div className="oc4-result is-empty">
        <p>No result yet — press a move above.</p>
      </div>
    );
  }

  if (kind === "ballpark") {
    return (
      <div className="oc4-result oc4-at-5">
        <div className="oc4-result-line">
          <b>Ballpark built</b> — {money(price)} at {(margin * 100).toFixed(1)}% margin, based on 3 comparable jobs.
        </div>
        <button className="oc4-reason-toggle" onClick={onToggleReasoning}>
          {reasoningOpen ? "Hide reasoning" : "View reasoning"}
        </button>
        {reasoningOpen ? (
          <p className="oc4-reason-body">
            Priced from 3 comparable jobs — Sephora's Rouge endcap, Ulta's Fragrance bay, Nike's Kids footwear wall —
            plus the standard rate table for materials. Labor is routed across assembly, fabrication, finishing, QC
            and install; margin applied at the endcap band's 55% target.
          </p>
        ) : null}
      </div>
    );
  }

  if (kind === "compare") {
    return (
      <div className="oc4-result oc4-at-7">
        <div className="oc4-result-line">
          <b>3 comparable jobs found</b> — assembly rate is {COMP_PCT_OVER}% above the average of ${COMP_AVG.toFixed(2)}
          /hr.
        </div>
        <table className="oc4-comp-table">
          <tbody>
            {COMPS.map((c) => (
              <tr key={c.job}>
                <td>{c.job}</td>
                <td>${c.rate.toFixed(2)}/hr</td>
              </tr>
            ))}
            <tr className="oc4-comp-avg">
              <td>Average</td>
              <td>${COMP_AVG.toFixed(2)}/hr</td>
            </tr>
          </tbody>
        </table>
        <div className="oc4-result-actions">
          <button className="oc4-action-primary" onClick={onApplyAverage}>
            Apply the average instead
          </button>
        </div>
      </div>
    );
  }

  if (kind === "applied") {
    const price0 = PRICE_ORIGINAL;
    const marginNow = (price0 - totalCost(true)) / price0;
    return (
      <div className="oc4-result oc4-at-8">
        <div className="oc4-toast">
          <span className="oc4-toast-check">✓</span>
          Applied — assembly now ${COMP_AVG.toFixed(2)}/hr, was ${ASSEMBLY_RATE_ORIG.toFixed(2)}/hr.
        </div>
        <div className="oc4-result-line">
          <b>Ballpark built</b> — {money(price0)} at {(marginNow * 100).toFixed(1)}% margin, based on 3 comparable jobs.
        </div>
        <p className="oc4-result-delta">
          What changed: assembly rate corrected, cost down {money((ASSEMBLY_RATE_ORIG - COMP_AVG) * UNITS)}.
        </p>
      </div>
    );
  }

  if (kind === "recalc-prompt") {
    return (
      <div className="oc4-result oc4-at-9">
        <div className="oc4-result-line">
          <b>Recalculate at New Price</b> — the client pushed back. Type the target.
        </div>
        <div className="oc4-inline-field">
          <span>$</span>
          <span className="oc4-inline-field-placeholder">target price…</span>
          <button className="oc4-action-primary is-ghost" disabled>
            Confirm
          </button>
        </div>
      </div>
    );
  }

  if (kind === "recalc-running") {
    return (
      <div className="oc4-result oc4-at-10">
        <div className="oc4-result-line">
          <b>Recalculate at New Price</b> — the client pushed back. Type the target.
        </div>
        <div className="oc4-inline-field is-set">
          <span>$</span>
          <span className="oc4-inline-field-value">145,000</span>
          <button className="oc4-action-primary" onClick={onConfirmRecalc}>
            Confirm
          </button>
        </div>
        <div className="oc4-running">
          <span className="oc4-running-pulse" /> Recalculating margin across every line…
        </div>
      </div>
    );
  }

  // kind === "recalc-warn" or "held"
  const isHeld = kind === "held";
  return (
    <div className={`oc4-result ${isHeld ? "" : "is-warn"} oc4-at-11`}>
      <div className="oc4-result-line">
        <b>Recalculated at {money(PRICE_PUSHBACK)}</b> — margin is now {(margin * 100).toFixed(1)}%, under your{" "}
        {Math.round(MARGIN_FLOOR * 100)}% floor.{" "}
        {isHeld ? "Held for review." : "This needs someone to say yes."}
      </div>
      <p className="oc4-result-delta">
        What changed: {money(PRICE_ORIGINAL)} → {money(PRICE_PUSHBACK)} · margin {(marginBeforeRecalc * 100).toFixed(1)}% →{" "}
        {(margin * 100).toFixed(1)}%.
      </p>
      {!isHeld && showActions ? (
        <div className="oc4-result-actions oc4-at-12">
          <button className="oc4-action-primary" onClick={onAcceptAnyway}>
            Accept anyway
          </button>
          <button className="oc4-action-secondary" onClick={onHoldForReview}>
            Hold for review
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Closing() {
  return (
    <div className="oc4-closing oc4-at-14">
      <div className="oc4-closing-card">
        <div className="oc4-closing-kicker">Outform — Concept 4</div>
        <h2>A fixed set of moves, not a conversation to have.</h2>
        <p>
          Press the one you need, see what changed, nothing to scroll back through. No thread grows in this panel —
          each button acts on the whole document and leaves one result line behind it, with the one or two actions
          that result actually calls for.
        </p>
        <div className="oc4-closing-split">
          <div>
            <b>Fixed buttons</b>
            <span>Always the same four. Never a growing list, never a prompt to write.</span>
          </div>
          <div>
            <b>One result line</b>
            <span>Replaced, not appended. What's on screen is the current answer, not a log of every answer.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutformConceptFour;
